/**
 * Hook for managing grant access form state and submission
 * Consolidates form logic with validation
 */

import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import { grantEmployeeAccessSchema, type GrantEmployeeAccessFormData } from "@/lib/types/auth/grant-access";

export function useGrantAccessForm(initialContactIds: string[] = []) {
  const form = useForm<GrantEmployeeAccessFormData>({
    resolver: zodResolver(grantEmployeeAccessSchema),
    defaultValues: {
      userId: "",
      contactIds: initialContactIds.length > 0 ? initialContactIds : [],
      grantToAllClients: false,
    },
  });

  const resetForm = React.useCallback(() => {
    form.reset({
      userId: "",
      contactIds: [],
      grantToAllClients: false,
    });
  }, [form]);

  const setContactIds = React.useCallback(
    (contactIds: string[]) => {
      form.setValue("contactIds", contactIds);
    },
    [form]
  );

  const setGrantToAllClients = React.useCallback(
    (value: boolean) => {
      form.setValue("grantToAllClients", value);
      if (value) {
        form.setValue("contactIds", []);
      }
    },
    [form]
  );

  const setUserId = React.useCallback(
    (userId: string) => {
      form.setValue("userId", userId);
    },
    [form]
  );

  return {
    form,
    resetForm,
    setContactIds,
    setGrantToAllClients,
    setUserId,
  };
}
